import { FormControlLabel, FormLabel, Switch as SwitchRoot, SxProps } from '@mui/material'
import { Controller, FieldPath, FieldValues } from 'react-hook-form'

import { FlexBox } from 'components/Box'
import Tooltip from 'components/Tooltip'
import { DEFAULT_TOOLTIP_MODE } from './constants'
import { SwitchProps } from './types'

const prepareValue = <T extends FieldValues>(
  fieldValue: boolean,
  componentValue: SwitchProps<T>['value'],
): boolean | undefined => {
  if (fieldValue !== undefined) {
    return fieldValue
  } else if (typeof componentValue === 'boolean' && componentValue !== undefined) {
    return componentValue
  } else return false
}

export const Switch = <T extends FieldValues & { uiValueOverride?: boolean }>(
  props: SwitchProps<T> & { uiValueOverride?: boolean },
): React.JSX.Element => {
  const {
    bold,
    control,
    disabled,
    label,
    labelPlacement = 'end',
    name,
    tooltip,
    tooltipDisabled,
    tooltipMode = DEFAULT_TOOLTIP_MODE,
    uiValueOverride,
    ...muiSwitchProps
  } = props

  const SwitchWithTooltip = (SwitchControlled: React.JSX.Element): React.JSX.Element => {
    if (tooltip && tooltipMode === 'wrap') {
      return <Tooltip {...tooltip}>{SwitchControlled}</Tooltip>
    } else if (tooltipDisabled) {
      return <Tooltip {...tooltipDisabled}>{SwitchControlled}</Tooltip>
    } else return SwitchControlled
  }

  const cursorStyle: SxProps = {
    cursor: disabled ? 'default' : 'pointer',
  }

  return (
    <FormControlLabel
      control={
        <Controller
          control={control}
          name={name as FieldPath<T>}
          render={({ field }) => {
            const SwitchControlled: React.JSX.Element = (
              <SwitchRoot
                {...field}
                {...muiSwitchProps}
                checked={
                  uiValueOverride !== undefined
                    ? uiValueOverride
                    : prepareValue(field.value, muiSwitchProps.value)
                }
                disabled={disabled}
                id={name}
              />
            )

            return (
              <FlexBox
                axis='x'
                sx={{ ...cursorStyle }}
              >
                {SwitchWithTooltip(SwitchControlled)}
                {tooltip && tooltipMode === 'icon' && <Tooltip {...tooltip} />}
              </FlexBox>
            )
          }}
        />
      }
      label={
        <FormLabel
          sx={{
            fontWeight: theme => (bold ? 'bold' : theme.typography.fontWeightRegular),
          }}
        >
          {label}
        </FormLabel>
      }
      labelPlacement={labelPlacement}
      sx={{
        ml: 0,
        mr: label ? 1 : 0,
        width: 'fit-content',
      }}
    />
  )
}
